import { isAdmin } from '@/constants/roles';
import { Podcast, RefreshCw, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useRecoilState } from 'recoil';
import { toast } from 'sonner';

import {
    useChatData,
    useChatMessages,
    useConfig
} from '@chainlit/react-client';

import {
    DropdownMenuItem,
    DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';

import { useAuth } from '@/hooks/useAuth';

import { podcastState } from '@/state/podcast';

interface DMPodcastMenuItemProps {
    onCloseDropdown: () => void;
}

export default function DMPodcastMenuItem({
    onCloseDropdown
}: DMPodcastMenuItemProps) {
    const { threadId } = useChatMessages();
    const { loading: chatLoading } = useChatData();
    const { config } = useConfig();
    const { user } = useAuth();
    const [podcast, setPodcast] = useRecoilState(podcastState);
    const [isAuthorized, setIsAuthorized] = useState(false);
    const abortControllerRef = useRef<AbortController | null>(null);

    useEffect(() => {
        if (!user) {
            setIsAuthorized(false);
            return;
        }
        const userIsAdmin = isAdmin(user?.metadata?.organization_roles);
        const isEnabledForOrg = config?.features?.podcast_enabled !== false;
        setIsAuthorized(userIsAdmin && isEnabledForOrg);
    }, [user, config]);

    const handleCancelGeneration = useCallback(async () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
        if (threadId) {
            fetch(`/podcast/cancel/${threadId}`, {
                method: 'POST',
                credentials: 'include'
            }).catch(() => {});
        }
        setPodcast((prev) => ({ ...prev, isGenerating: false, error: null }));
    }, [setPodcast, threadId]);

    const handleGeneratePodcast = useCallback(async () => {
        if (!threadId) {
            toast.error('No thread available to generate podcast');
            return;
        }

        onCloseDropdown();
        abortControllerRef.current = new AbortController();
        setPodcast((prev) => ({ ...prev, isGenerating: true, error: null }));

        try {
            const response = await fetch('/podcast/generate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ thread_id: threadId }),
                signal: abortControllerRef.current.signal
            });

            if (!response.ok) {
                const errorData = await response
                    .json()
                    .catch(() => ({ detail: 'Unknown error' }));
                throw new Error(
                    errorData.detail || 'Failed to generate podcast'
                );
            }

            const result = await response.json();

            setPodcast((prev) => ({
                ...prev,
                isGenerating: false,
                status: {
                    has_podcast: true,
                    is_outdated: false,
                    podcast_url: result.podcast_url,
                    created_at: result.created_at,
                    last_message_at: result.last_message_at,
                    current_last_message_at: result.last_message_at,
                    size_bytes: result.size_bytes,
                    file_extension: result.file_extension || 'mp3',
                    podcast_name: result.podcast_name
                },
                error: null
            }));

            toast.success('Podcast generated successfully!');
        } catch (error: any) {
            if (error.name === 'AbortError') {
                setPodcast((prev) => ({ ...prev, isGenerating: false }));
                return;
            }
            console.error('Error generating podcast:', error);
            setPodcast((prev) => ({
                ...prev,
                isGenerating: false,
                error: error.message || 'Failed to generate podcast'
            }));
            toast.error(
                error.message || 'Failed to generate podcast. Please try again.'
            );
        } finally {
            abortControllerRef.current = null;
        }
    }, [threadId, setPodcast, onCloseDropdown]);

    const isGenerating = podcast.isGenerating;
    const isLoading = podcast.isLoading;
    const isPodcastDisabled = !threadId || isLoading || chatLoading;
    const hasPodcast = podcast.status?.has_podcast;
    const isVisible = isAuthorized && !hasPodcast;

    if (!isVisible) return null;

    return (
        <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
                variant={isGenerating ? 'destructive' : 'default'}
                onClick={
                    isGenerating
                        ? handleCancelGeneration
                        : handleGeneratePodcast
                }
                disabled={isPodcastDisabled && !isGenerating}
            >
                {isGenerating ? (
                    <>
                        <RefreshCw className="animate-spin" />
                        <span>
                            Generating podcast...
                        </span>
                        <X className="opacity-60 ml-auto" />
                    </>
                ) : isLoading ? (
                    <>
                        <RefreshCw className="animate-spin" />
                        <span>Loading...</span>
                    </>
                ) : (
                    <>
                        <Podcast />
                        <span>
                            Generate podcast
                        </span>
                    </>
                )}
            </DropdownMenuItem>
        </>
    );
}
